import { useEffect, useRef, useState } from "react";
import BulkStepLayout from "../../pages/BulkStepLayout";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Backdrop, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { BudgetBucketNodeItem, BudgetItem, BucketNodeItem, BucketNodeSnapshotResponse, CurrentUser, FamilyBucketLabel, FamilyBucketPluralLabel, SortBucketNodesHierarchically } from "../../types";
import Dropdown from "../Inputs/Dropdown";
import { useItems } from "../../features/Fetch/Items";
import { useNavigate } from "react-router-dom";

const BulkBudgetBucketNodeLinker = (props: any) => {
  const { step, onBack, steps } = props;
  const navigate = useNavigate();
  const budgets: BudgetItem[] = useSelector((state: any) => state.budgets.value);
  const bucketNodes: BucketNodeSnapshotResponse | undefined = useSelector((state: any) => state.bucketNodes.value);
  const user: CurrentUser = useSelector((state: any) => state.userData.value);
  const repo = useItems();
  const linkableBuckets = (user.bucketDefinitions?.definitions ?? [])
    .filter(definition => definition.isEnabled && definition.requiresBudgetMapping)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const sortedNodes: BucketNodeItem[] = [...(bucketNodes?.nodes ?? [])].sort((a, b) => {
    const typeCompare = a.bucketTypeKey.localeCompare(b.bucketTypeKey);
    return typeCompare !== 0 ? typeCompare : a.displayName.localeCompare(b.displayName);
  });
  const nodesByBucket = linkableBuckets.reduce((result: { [key: string]: BucketNodeItem[] }, definition) => {
    result[definition.bucketTypeKey] = SortBucketNodesHierarchically(
      sortedNodes.filter(node => node.bucketTypeKey.toLowerCase() === definition.bucketTypeKey.toLowerCase()),
      bucketNodes?.relations);
    return result;
  }, {} as { [key: string]: BucketNodeItem[] });
  const nodeById = new Map<number, BucketNodeItem>(sortedNodes.map(node => [node.bucketNodeId, node]));
  const budgetLabel = FamilyBucketLabel(user, "budget", "Budget");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<BudgetItem[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (budgets && bucketNodes) {
      setRows(budgets.map(b => ({ ...b })));
      setLoading(false);
    }
  }, [budgets, bucketNodes]);

  if (loading) return <p>Loading...</p>;

  return (
    <BulkStepLayout step={step} onBack={onBack} steps={steps}>
      <Paper elevation={0} style={{ padding: 8, backgroundColor: 'azure', paddingBottom: 64 }}>
        <h2 className="bulk-category-title">Bulk {budgetLabel} Linker</h2>
        <TableContainer>
          <Table size="small" className="fixed-width-table" sx={{ border: 0 }}>
            <TableHead>
              <TableRow>
                <TableCell className="compact-table-cell header" align="right">{budgetLabel}</TableCell>
                {linkableBuckets.map(definition => (
                  <TableCell key={definition.bucketTypeKey} className="compact-table-cell header">
                    {FamilyBucketPluralLabel(user, definition.bucketTypeKey, definition.pluralLabel)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((budget, idx) => (
                <TableRow key={budget.budgetId} sx={{ backgroundColor: idx % 2 === 1 ? '#ddffff' : 'white' }}>
                  <TableCell className="compact-table-cell bottomless px-10" align="right">
                    {budget.description}
                  </TableCell>
                  {linkableBuckets.map(definition => (
                    <TableCell key={`${budget.budgetId}-${definition.bucketTypeKey}`} className="compact-table-cell bottomless">
                      <Dropdown
                        HideLabel
                        NoMB
                        Variant="standard"
                        Multiple
                        Options={(nodesByBucket[definition.bucketTypeKey] ?? []).map(node => ({ Id: node.bucketNodeId, Value: node.displayName }))}
                        Value={GetBucketNodeIds(budget, definition.bucketTypeKey)}
                        OnChange={(newIds: number[]) => {
                          const updatedRows = rows.map((row, rowIndex) =>
                            rowIndex === idx ? { ...row, bucketNodes: BuildBudgetBucketNodes(row, definition.bucketTypeKey, newIds, nodeById) } : row
                          );
                          setRows(updatedRows);
                        }}
                        Size={12}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <div className="bulk-budget-action-row">
          <Button
            variant="outlined"
            color="error"
            onClick={onBack}
            style={{ minWidth: 160, padding: '4px 16px' }}>
            Back
          </Button>
          <div className="bulk-budget-action-buttons">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              style={{ minWidth: 120, padding: '4px 16px' }}
              disabled={!hasChanges() || saving}>
              Save and Exit
            </Button>
          </div>
        </div>
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading || saving}>
          <CircularProgress />
        </Backdrop>
      </Paper>
    </BulkStepLayout>
  );

  function hasChanges() {
    if (budgets.length !== rows.length) return true;

    for (let i = 0; i < budgets.length; i++) {
      for (const definition of linkableBuckets) {
        const originalIds = GetBucketNodeIds(budgets[i], definition.bucketTypeKey);
        const currentIds = GetBucketNodeIds(rows[i], definition.bucketTypeKey);
        if (originalIds.length !== currentIds.length || originalIds.some((id, idx) => id !== currentIds[idx])) {
          return true;
        }
      }
    }

    return false;
  }

  async function handleSave() {
    setSaving(true);

    try {
      const changed = rows.filter((row, index) => {
        return linkableBuckets.some(definition => {
          const originalIds = GetBucketNodeIds(budgets[index], definition.bucketTypeKey);
          const currentIds = GetBucketNodeIds(row, definition.bucketTypeKey);
          return originalIds.length !== currentIds.length || originalIds.some((id, idx) => id !== currentIds[idx]);
        });
      });

      for (const budget of changed) {
        await repo.UpdateBudgetNodesAsync({ ...budget, bucketNodes: budget.bucketNodes });
      }

      await repo.GetBudgetsAsync();
      navigate('/budgets');
    } finally {
      setSaving(false);
    }
  }
};

function GetBucketNodeIds(budget: BudgetItem, bucketTypeKey: string) {
  return [...(budget.bucketNodes ?? [])]
    .filter(node => node.bucketTypeKey.toLowerCase() === bucketTypeKey.toLowerCase())
    .map(node => node.bucketNodeId)
    .sort((a, b) => a - b);
}

function BuildBudgetBucketNodes(budget: BudgetItem, bucketTypeKey: string, bucketNodeIds: number[], nodeById: Map<number, BucketNodeItem>) {
  const existingTypeNodes = (budget.bucketNodes ?? []).filter(node => node.bucketTypeKey.toLowerCase() === bucketTypeKey.toLowerCase());
  const otherBucketNodes = (budget.bucketNodes ?? []).filter(node => node.bucketTypeKey.toLowerCase() !== bucketTypeKey.toLowerCase());
  const nextTypeNodes = bucketNodeIds
    .map(bucketNodeId => {
      const existing = existingTypeNodes.find(node => node.bucketNodeId === bucketNodeId);
      if (existing) return existing;

      const bucketNode = nodeById.get(bucketNodeId);
      if (!bucketNode) return null;

      return {
        budgetBucketNodeId: 0,
        bucketNodeId: bucketNode.bucketNodeId,
        bucketTypeKey: bucketNode.bucketTypeKey,
        displayName: bucketNode.displayName,
        isInactive: bucketNode.isInactive,
        isViewTransactions: bucketNode.isViewTransactions,
        startDate: null,
        endDate: null,
      } as BudgetBucketNodeItem;
    })
    .filter((node): node is BudgetBucketNodeItem => node !== null);

  return [...otherBucketNodes, ...nextTypeNodes];
}

export default BulkBudgetBucketNodeLinker;
